import type { Questionnaire } from '@l2u/ui/lib/types.ts';
import type { QuestionnaireVendorStatus } from '../lib/questionnaireStatus.ts';

type Props = {
  questionnaires: Questionnaire[];
  /** Vendor-action status per questionnaire id (built by the wizard). */
  statusById: Map<string, QuestionnaireVendorStatus>;
  onSelect: (id: string) => void;
};

const PILL_BASE = 'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium';

function StatusPill({ status }: { status: QuestionnaireVendorStatus }) {
  if (status.state === 'action_needed') {
    return (
      <span className={`${PILL_BASE} bg-red-50 text-red-700`}>
        Action required · {status.actionCount}
      </span>
    );
  }
  if (status.state === 'awaiting_response') {
    return (
      <span className={`${PILL_BASE} bg-amber-50 text-amber-700`}>⏳ Awaiting your response</span>
    );
  }
  if (status.state === 'pending_submission') {
    return (
      <span className={`${PILL_BASE} bg-indigo-50 text-indigo-700`}>📝 Pending submission</span>
    );
  }
  if (status.state === 'under_review') {
    return (
      <span className={`${PILL_BASE} bg-blue-50 text-blue-700`}>🔍 Under Review by Admin</span>
    );
  }
  return <span className={`${PILL_BASE} bg-emerald-50 text-emerald-700`}>✓ Compliant</span>;
}

/**
 * Landing screen shown to vendors who belong to more than one applicable
 * questionnaire. The vendor picks a type here; the main Questionnaire wizard
 * then scopes to only that questionnaire's questions. Each card shows a status
 * pill so the vendor can see which questionnaires still need their attention.
 */
export function QuestionnaireTypePicker({ questionnaires, statusById, onSelect }: Props) {
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold tracking-tight" style={{ color: 'var(--brand-ink)' }}>
          Select a questionnaire
        </h2>
        <p className="mt-1 text-sm text-slate-500">
          Choose which questionnaire you would like to complete.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {questionnaires.map((q) => {
          const status = statusById.get(q.id);
          return (
            <button
              key={q.id}
              type="button"
              onClick={() => onSelect(q.id)}
              className="rounded-xl border border-slate-200 bg-white p-5 text-left transition hover:border-[#4D148C] hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4D148C]"
            >
              <div className="text-base font-semibold" style={{ color: 'var(--brand-ink)' }}>
                {q.name}
              </div>
              {q.description && <div className="mt-1 text-sm text-slate-500">{q.description}</div>}
              {status && (
                <div className="mt-3">
                  <StatusPill status={status} />
                </div>
              )}
              <div className="mt-3 text-xs font-medium" style={{ color: 'var(--brand-primary)' }}>
                Open →
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
